<template>
  <div>
    <v-dialog persistent v-model="dialog" max-width="950" scrollable>
      <v-card class="filter-card">
        <v-card-title class="filter-header">
          <div class="d-flex align-center">
            <v-icon class="mr-2" color="white">mdi-filter-variant</v-icon>
            <span class="headline font-weight-bold white--text">Patient Filter</span>
          </div>
          <v-btn 
            @click="cancel" 
            class="white--text close-header-btn" 
            icon
            large
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-6">
          <!-- Search by Name Section -->
          <div class="filter-section mb-6">
            <div class="section-title mb-4">
              <v-icon class="mr-2" color="#aa00ff">mdi-account-search</v-icon>
              <span class="subtitle-1 font-weight-bold">Search by Name</span>
            </div>
            <v-row align="center">
              <v-col cols="12" md="8">
                <v-text-field
                  v-model="search"
                  placeholder="Enter patient name..."
                  outlined
                  prepend-inner-icon="mdi-account"
                  clearable
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4" class="text-md-right">
                <v-btn
                  @click="searchByText"
                  class="search-btn-purple"
                  large
                  block
                  :disabled="!search"
                  dark
                >
                  <v-icon left>mdi-magnify</v-icon>
                  Search
                </v-btn>
              </v-col>
            </v-row>
          </div>

          <v-divider class="my-6"></v-divider>

          <!-- Search by MDRTB Code Section -->
          <div class="filter-section mb-6">
            <div class="section-title mb-4">
              <v-icon class="mr-2" color="#aa00ff">mdi-identifier</v-icon>
              <span class="subtitle-1 font-weight-bold">Search by MDRTB Code</span>
            </div>
            <v-row align="center">
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="mdrtb_reg_no"
                  type="number"
                  placeholder="Registration No"
                  outlined
                  prepend-inner-icon="mdi-numeric"
                  clearable
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="mdrtb_reg_year"
                  type="number"
                  placeholder="Registration Year"
                  outlined
                  prepend-inner-icon="mdi-calendar"
                  clearable
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-btn
                  @click="searchByCode"
                  class="search-btn-purple"
                  large
                  block
                  :disabled="!mdrtb_reg_no || !mdrtb_reg_year"
                  dark
                >
                  <v-icon left>mdi-magnify</v-icon>
                  Search
                </v-btn>
              </v-col>
            </v-row>
          </div>

          <v-divider class="my-6"></v-divider>

          <!-- Filter by Support Type Section -->
          <div class="filter-section mb-6">
            <div class="section-title mb-4">
              <v-icon class="mr-2" color="#aa00ff">mdi-account-check</v-icon>
              <span class="subtitle-1 font-weight-bold">Filter by Support Type</span>
            </div>
            <v-row>
              <v-col cols="12" md="3">
                <v-select
                  v-model="support_type"
                  placeholder="Enroll Type"
                  outlined
                  item-text="name"
                  item-value="id"
                  :items="supportTypes"
                  prepend-inner-icon="mdi-format-list-bulleted"
                  clearable
                ></v-select>
              </v-col>
              <v-col cols="12" md="3">
                <v-menu
                  v-model="supportStartDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="supportStartDate"
                      placeholder="Start Date"
                      outlined
                      readonly
                      prepend-inner-icon="mdi-calendar-start"
                      v-bind="attrs"
                      v-on="on"
                      clearable
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="supportStartDate"
                    @input="supportStartDateMenu = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" md="3">
                <v-menu
                  v-model="supportEndDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="supportEndDate"
                      placeholder="End Date"
                      outlined
                      readonly
                      prepend-inner-icon="mdi-calendar-end"
                      v-bind="attrs"
                      v-on="on"
                      clearable
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="supportEndDate"
                    @input="supportEndDateMenu = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" md="3">
                <v-btn
                  @click="searchBySupportType"
                  class="search-btn-purple"
                  large
                  block
                  :disabled="!support_type || !supportStartDate || !supportEndDate"
                  dark
                >
                  <v-icon left>mdi-magnify</v-icon>
                  Search
                </v-btn>
              </v-col>
            </v-row>
          </div>

          <v-divider class="my-6"></v-divider>

          <!-- Filter by Nominated Date Section -->
          <div class="filter-section">
            <div class="section-title mb-4">
              <v-icon class="mr-2" color="#aa00ff">mdi-calendar-check</v-icon>
              <span class="subtitle-1 font-weight-bold">Filter by Nominated Date</span>
            </div>
            <v-row>
              <v-col cols="12" md="4">
                <v-select
                  :items="months"
                  v-model="nominated_month"
                  item-text="text"
                  item-value="value"
                  placeholder="Select Month"
                  outlined
                  prepend-inner-icon="mdi-calendar-month"
                  clearable
                ></v-select>
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  :items="years"
                  v-model="nominated_year"
                  item-text="text"
                  item-value="value"
                  placeholder="Select Year"
                  outlined
                  prepend-inner-icon="mdi-calendar"
                  clearable
                ></v-select>
              </v-col>
              <v-col cols="12" md="4">
                <v-btn
                  @click="searchByNomated"
                  class="search-btn-purple"
                  large
                  block
                  :disabled="!nominated_month || !nominated_year"
                  dark
                >
                  <v-icon left>mdi-magnify</v-icon>
                  Search
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-btn
            @click="clearAllFilters"
            text
            color="grey darken-1"
            class="mr-2"
          >
            <v-icon left small>mdi-filter-off</v-icon>
            Clear All
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            @click="cancel"
            outlined
            color="grey darken-1"
            class="mr-2"
          >
            <v-icon left>mdi-close</v-icon>
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState } from "vuex";
export default {
  props: {
    dialog: {
      type: Boolean,
    },
  },
  data: () => ({
    search: "",
    supportStartDateMenu: false,
    supportStartDate: "",
    supportEndDateMenu: false,
    supportEndDate: "",
    supportTypes: [
      { id: 1, name: "Pre-Enroll" },
      { id: 2, name: "Enroll" },
    ],
    support_type: "",
    mdrtb_reg_no: "",
    mdrtb_reg_year: "",
    nominated_month: "",
    nominated_year: "",
    nominated_type: "",
    onGoingEndDate: "",
    onGoingEndDateMenu: "",
  }),
  computed: {
    ...mapState("InitialData", ["months"]),
    ...mapState("InitialData", ["years"]),
  },
  methods: {
    cancel() {
      this.$emit("cancel");
    },
    searchByText() {
      if (this.search) {
        this.$router.push(`/patient?search=${this.search}&page=1`);
        this.$emit("cancel");
      }
    },
    searchBySupportType() {
      if (this.support_type && this.supportStartDate && this.supportEndDate) {
        this.$router.push(
          `/patient?support_type=${this.support_type.id}&support_startdate=${this.supportStartDate}&support_enddate=${this.supportEndDate}&page=1`
        );
        this.$emit("cancel");
      }
    },
    searchByCode() {
      if (this.mdrtb_reg_no && this.mdrtb_reg_year) {
        this.$router.push(
          `/patient?mdrtb_reg_no=${this.mdrtb_reg_no}&mdrtb_reg_year=${this.mdrtb_reg_year}&page=1`
        );
        this.$emit("cancel");
      }
    },
    searchByNomated() {
      if (this.nominated_month && this.nominated_year) {
        this.$router.push(
          `/patient?nominated_month=${this.nominated_month}&nominated_year=${this.nominated_year}&page=1`
        );
        this.$emit("cancel");
      }
    },
    searchForOnging() {
      if (this.onGoingEndDate) {
        this.$router.push(`/patient?onGoingEnddate=${this.onGoingEndDate}&page=1`);
        this.$emit("cancel");
      }
    },
    clearAllFilters() {
      this.search = null;
      this.support_type = null;
      this.supportStartDate = null;
      this.supportEndDate = null;
      this.mdrtb_reg_no = null;
      this.mdrtb_reg_year = null;
      this.nominated_month = null;
      this.nominated_year = null;
      this.onGoingEndDate = null;
    },
  },
};
</script>

<style scoped>
.filter-card {
  border-radius: 12px;
  overflow: hidden;
}

.filter-header {
  background: linear-gradient(135deg, #aa00ff 0%, #8e00cc 100%);
  padding: 20px 24px;
  box-shadow: 0 2px 8px rgba(170, 0, 255, 0.2);
}

.close-header-btn {
  transition: all 0.3s ease;
}

.close-header-btn:hover {
  background-color: rgba(255, 255, 255, 0.2) !important;
  transform: rotate(90deg);
}

/* .filter-section {
  padding: 16px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #aa00ff;
} */

.section-title {
  display: flex;
  align-items: center;
  color: #424242;
  margin-bottom: 16px;
}

.section-title .v-icon {
  font-size: 24px;
}

.v-select,
.v-combobox,
.v-text-field {
  border-radius: 6px;
}

.v-btn {
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.v-btn:not(.v-btn--disabled) {
  transition: all 0.3s ease;
}

.v-btn:not(.v-btn--disabled):hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(170, 0, 255, 0.3);
}

.search-btn-purple {
  background: linear-gradient(135deg, #aa00ff 0%, #8e00cc 100%) !important;
  color: white !important;
  box-shadow: 0 2px 8px rgba(170, 0, 255, 0.3) !important;
}

.search-btn-purple:hover:not(.v-btn--disabled) {
  transform: translateY(-2px) !important;
  box-shadow: 0 4px 12px rgba(170, 0, 255, 0.4) !important;
}

.v-card-actions {
  background-color: #fafafa;
}

@media (max-width: 960px) {
  .filter-section {
    margin-bottom: 24px;
  }
  
  .v-btn {
    margin-top: 8px;
  }
}
</style>
